import { useSelector } from 'react-redux'

import { useAppDispatch, useAppSelector } from 'src/Hooks/redux'
import { getUser, getUserId } from 'src/Redux/auth-process/userSlice/selectors'
import { CampaignPlatformTypeEnum, CampaignTypeEnum } from 'src/Types/campaigns'
import { useSpotifyCampaignStatus } from 'src/Redux/spotifyCampaignStatusApi'
import {
  checkoutViewEvent,
  checkoutConfirmedPayment,
} from 'src/Helpers/TagManager'
import { getAllCampaignsCount, useMetadataQuery } from 'src/Redux/metadataApi'
import { PaymentMethodEnum } from 'src/Constants/enums'
import {
  sendPaymentInfoEvent,
  sendPaymentStartEvent,
  sendPaymentStartPlatformEvent,
} from 'src/Redux/googleAnalytics-process/api-actions'
import { useCheckoutStartMutation } from 'src/Redux/analyticsApi'
import { getTiktokPrice } from 'src/Redux/schedule-process/audienceTiktokSlice/selectors'
import { getScheduledTikTokCampaign } from 'src/Redux/schedule-process/tiktokCampaign/selectors'
import { getPaymentMethod } from 'src/Redux/payment-process/selectors'
import { getRememberPayPalForFutureFlag } from 'src/Redux/paypalTransactions-process/selectors'
import { useSendPaymentDataMutation } from 'src/Redux/paymentWebhookDataApi'
import { useGetSubscriptionQuery } from 'src/Redux/subscription-process/subscriptionApi'
import { spotifyTotalPriceSelector } from 'src/Redux/spotifyCampaignMatchingProcess'

interface CheckoutEventsBody {
  platformName: string
  id: number
  campaignType: string
  genres: string[]
  languages?: string[]
  resultPrice: number
  startDate: string
}

interface CheckoutEvents {
  handleCheckoutStartEvent: (params: CheckoutEventsBody) => void
  handleSpotifyCheckoutConfirmedPaymentEvent: (
    overridePaymentMethod?: PaymentMethodEnum,
  ) => void
  handleTiktokCheckoutConfirmedPaymentEvent: (
    overridePaymentMethod?: PaymentMethodEnum,
  ) => void
  sendPaymentDataToWebhook: ({
    platform,
    selectedPaymentMethod,
  }: {
    platform: CampaignPlatformTypeEnum
    selectedPaymentMethod: PaymentMethodEnum
  }) => void
}

export const useCheckoutEvents = (): CheckoutEvents => {
  const dispatch = useAppDispatch()
  const [checkoutStart] = useCheckoutStartMutation()
  const user = useSelector(getUser)
  const userId = useSelector(getUserId)
  const { data: plan } = useGetSubscriptionQuery()
  const { data: campaigns } = useMetadataQuery(null)

  const amountOfCampaigns = getAllCampaignsCount(campaigns)
  const price = useAppSelector(getTiktokPrice)
  const { campaignDetails } = useSpotifyCampaignStatus()
  const spotifyTotalPrice = useAppSelector(spotifyTotalPriceSelector)
  const scheduledCampaign = useSelector(getScheduledTikTokCampaign)
  const paymentMethod = useSelector(getPaymentMethod)
  const isRememberPayPalForFuture = useSelector(getRememberPayPalForFutureFlag)
  const [sendPaymentData] = useSendPaymentDataMutation()

  const handleCheckoutStartEvent = (params: CheckoutEventsBody): void => {
    checkoutViewEvent({
      platformName: params.platformName,
      campaignId: params.id,
      campaignType: params.campaignType,
      userId: String(userId),
      campaignNumber: amountOfCampaigns || 1,
      genreList: params.genres?.join(),
      resultPrice: params.resultPrice,
      paymentMethod: paymentMethod || PaymentMethodEnum.STRIPE,
      subscriptionPlan: plan?.package?.name || '',
    })

    void checkoutStart({
      campaignId: params.id,
      campaignPlatform: params.platformName,
      paymentMethod:
        paymentMethod === PaymentMethodEnum.STRIPE
          ? PaymentMethodEnum.STRIPE
          : PaymentMethodEnum.PAYPAL,
      price: params.resultPrice,
    })

    params.platformName === CampaignPlatformTypeEnum.SPOTIFY &&
      void dispatch(
        sendPaymentStartEvent({
          campaignId: params.id,
          url: window.location.origin,
        }),
      )

    params.platformName === CampaignPlatformTypeEnum.TIKTOK &&
      void dispatch(
        sendPaymentStartPlatformEvent({
          campaignId: params.id,
          platform: params.platformName,
          url: window.location.href,
        }),
      )
  }

  const handleSpotifyCheckoutConfirmedPaymentEvent = (
    overridePaymentMethod?: PaymentMethodEnum,
  ): void => {
    checkoutConfirmedPayment({
      isRememberPayPalForFuture,
      paymentMethod: overridePaymentMethod ?? paymentMethod,
      campaignId: Number(campaignDetails?.id),
      campaignType: campaignDetails?.type || CampaignTypeEnum.BASIC,
      platformName: CampaignPlatformTypeEnum.SPOTIFY,
      userId: String(userId),
      campaignNumber: amountOfCampaigns || 1,
      genreList:
        campaignDetails?.genres?.map((genre) => genre.name).join() || '',
      resultPrice: spotifyTotalPrice || 0,
      subscriptionPlan: plan?.package?.name || '',
    })
    void dispatch(
      sendPaymentInfoEvent({
        campaignID: Number(campaignDetails?.id),
        url: window.location.href,
      }),
    )
  }
  const handleTiktokCheckoutConfirmedPaymentEvent = (
    overridePaymentMethod?: PaymentMethodEnum,
  ): void => {
    checkoutConfirmedPayment({
      isRememberPayPalForFuture,
      paymentMethod: overridePaymentMethod ?? paymentMethod,
      campaignId: Number(scheduledCampaign.id),
      campaignType: scheduledCampaign.type || CampaignTypeEnum.BASIC,
      platformName: CampaignPlatformTypeEnum.TIKTOK,
      userId: String(userId),
      campaignNumber: amountOfCampaigns || 1,
      genreList: '',
      resultPrice: price,
      subscriptionPlan: plan?.package?.name || '',
    })
  }

  const sendPaymentDataToWebhook = ({
    platform,
    selectedPaymentMethod,
  }: {
    platform: CampaignPlatformTypeEnum
    selectedPaymentMethod: PaymentMethodEnum
  }): void => {
    const campaignId =
      platform === CampaignPlatformTypeEnum.TIKTOK
        ? scheduledCampaign.id
        : campaignDetails?.id

    void sendPaymentData({
      firstName: user.first_name,
      lastName: user.last_name,
      email: user.email,
      phone: user.phone_number,
      campaignId: Number(campaignId),
      paymentMethod: selectedPaymentMethod,
    })
  }

  return {
    handleCheckoutStartEvent,
    handleTiktokCheckoutConfirmedPaymentEvent,
    handleSpotifyCheckoutConfirmedPaymentEvent,
    sendPaymentDataToWebhook,
  }
}
